import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import MenuPage from "@/pages/MenuPage";
import HomePage from "@/pages/HomePage";
import DayUsePage from "@/pages/DayUsePage";
import TournamentPage from "@/pages/TournamentPage";
import TournamentManager from "@/components/TournamentManager";
import BarPage from "@/pages/BarPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminHome from "@/pages/AdminHome";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminShell from "@/components/AdminShell";
import AgendaView from "@/components/AgendaView";
import BlocksManager from "@/components/BlocksManager";
import UsersManager from "@/components/UsersManager";
import BarOrdersPanel from "@/components/BarOrdersPanel";
import ComandasManager from "@/components/ComandasManager";
import BarManager from "@/components/BarManager";
import StockManager from "@/components/StockManager";
import MenuBackgroundsManager from "@/components/MenuBackgroundsManager";
import HeroImageManager from "@/components/HeroImageManager";
import LogoManager from "@/components/LogoManager";
import SpaceImageManager from "@/components/SpaceImageManager";
import BannerManager from "@/components/BannerManager";
import ReviewsManager from "@/components/ReviewsManager";
import DayUseManager from "@/components/DayUseManager";
import PageLogoManager from "@/components/PageLogoManager";
import MenuVideoManager from "@/components/MenuVideoManager";
import ButtonTextsManager from "@/components/ButtonTextsManager";
import IconManager from "@/components/IconManager";
import MenuOrderManager from "@/components/MenuOrderManager";
import PhoneGate from "@/components/PhoneGate";
import PermissionsManager from "@/components/PermissionsManager";
import PageTextsManager from "@/components/PageTextsManager";
import GamesManager from "@/components/GamesManager";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b1610]">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-[#E8611A]" />
    </div>
  );
}

function Protected({ children, perm, owner, publicOk }) {
  const { user, checking } = useAuth();
  if (checking) return <Loader />;
  if (!user) return publicOk ? children : <Navigate to="/login" replace />;
  if (owner && !user.is_owner) return <Navigate to="/mind" replace />;
  if (perm && !(user.perms || []).some((p) => [].concat(perm).includes(p))) return <Navigate to={STAFF.includes(user.role) ? "/mind" : "/"} replace />;
  if (!STAFF.includes(user.role) && !(user.phone || "").replace(/\D/g, "")) return <PhoneGate />;
  return children;
}

const STAFF = ["admin", "subadmin", "bar", "professor", "admin_torneio"];
const ANY = ["reservas", "agenda", "usuarios", "cargos", "vendas", "produtos", "estoque", "horarios", "midia", "textos", "dayuse", "torneio"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Protected publicOk><MenuPage /></Protected>} />
      <Route path="/reservas" element={<Protected><HomePage /></Protected>} />
      <Route path="/dayuse" element={<Protected><DayUsePage /></Protected>} />
      <Route path="/torneio" element={<Protected><TournamentPage /></Protected>} />
      <Route path="/mind/torneio" element={<Protected perm="torneio"><AdminShell title="Torneios"><TournamentManager /></AdminShell></Protected>} />
      <Route path="/bar" element={<Protected><BarPage /></Protected>} />
      <Route path="/perfil" element={<Protected><ProfilePage /></Protected>} />

      <Route path="/mind" element={<Protected perm={ANY}><AdminHome /></Protected>} />
      <Route path="/mind/reservas" element={<Protected perm="reservas"><AdminDashboardPage /></Protected>} />
      <Route path="/mind/agenda" element={<Protected perm="agenda"><AdminShell title="Agenda do mês"><AgendaView /></AdminShell></Protected>} />
      <Route path="/mind/horarios" element={<Protected perm="horarios"><AdminShell title="Controle de Horários"><BlocksManager /></AdminShell></Protected>} />
      <Route path="/mind/usuarios" element={<Protected perm="usuarios"><AdminShell title="Usuários"><UsersManager /></AdminShell></Protected>} />
      <Route path="/mind/vendas" element={<Protected perm="vendas"><AdminShell title="Vendas do Bar"><BarOrdersPanel /><ComandasManager /></AdminShell></Protected>} />
      <Route path="/mind/produtos" element={<Protected perm="produtos"><AdminShell title="Cadastro de Produtos"><BarManager /></AdminShell></Protected>} />
      <Route path="/mind/estoque" element={<Protected perm="estoque"><AdminShell title="Estoque"><StockManager /></AdminShell></Protected>} />
      <Route path="/mind/textos" element={<Protected perm="textos"><AdminShell title="Textos das Páginas"><PageTextsManager /></AdminShell></Protected>} />
      <Route path="/mind/permissoes" element={<Protected owner><AdminShell title="Permissões"><PermissionsManager /></AdminShell></Protected>} />
      <Route path="/mind/midia" element={<Protected perm="midia"><AdminShell title="Logos, Imagens, Vídeos e Banners"><IconManager /><MenuOrderManager /><ButtonTextsManager /><MenuBackgroundsManager /><MenuVideoManager /><HeroImageManager /><LogoManager /><PageLogoManager title="Logo do Bar" flagKey="bar_logo" updatedKey="bar_logo_updated_at" serveUrl="/settings/bar-logo" uploadUrl="/admin/bar-logo" testid="bar-logo" /><SpaceImageManager /><BannerManager title="Publicidade — Reservas" hint="Aparecem na página de reservas, abaixo do topo." /><BannerManager placement="menu" title="Publicidade — Menu" hint="Aparecem no menu principal, abaixo da logo." /><BannerManager placement="bar" title="Publicidade — Bar" hint="Aparecem no Bar, acima dos produtos." /><ReviewsManager /></AdminShell></Protected>} />
      <Route path="/mind/dayuse" element={<Protected perm="dayuse"><AdminShell title="DayUse, Ranking e Jogos"><GamesManager /><DayUseManager /><PageLogoManager title="Logo do DayUse / Rotativo" flagKey="dayuse_logo" updatedKey="dayuse_logo_updated_at" serveUrl="/settings/dayuse-logo" uploadUrl="/admin/dayuse-logo" testid="dayuse-logo" /></AdminShell></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
